import React, { createContext, useContext, useEffect, useReducer, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthState, AuthContextType, LoginCredentials, User } from '../types/auth';
import authService, { AuthError } from '../services/authService';
import { registerSessionExpiredHandler } from '../services/apiClient';

// Initial state
const initialState: AuthState = {
  user: null,
  token: null,
  isLoading: true,
  isAuthenticated: false,
};

// Actions
type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_ERROR' }
  | { type: 'LOGOUT' }
  | { type: 'LOAD_USER'; payload: { user: User; token: string } }
  | { type: 'LOAD_ERROR' };

// Reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isLoading: false,
        isAuthenticated: true,
      };
    case 'LOGIN_ERROR':
      return {
        ...state,
        user: null,
        token: null,
        isLoading: false,
        isAuthenticated: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isLoading: false,
        isAuthenticated: false,
      };
    case 'LOAD_USER':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isLoading: false,
        isAuthenticated: true,
      };
    case 'LOAD_ERROR':
      return {
        ...state,
        user: null,
        token: null,
        isLoading: false,
        isAuthenticated: false,
      };
    default:
      return state;
  }
}

// Create context
const AuthContext = createContext<AuthContextType | null>(null);

// Provider component - needs to be wrapped with Router to use useNavigate
const AuthProviderInner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, dispatch] = useReducer(authReducer, initialState);
  const navigate = useNavigate();

  // Register session expiration handler on mount
  useEffect(() => {
    const handleSessionExpired = () => {
      console.log('Session expired - redirecting to login');
      // Clear state
      dispatch({ type: 'LOGOUT' });
      // Redirect with expiration message
      navigate('/login', {
        replace: true,
        state: {
          sessionExpired: true,
          message: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.'
        }
      });
    };

    registerSessionExpiredHandler(handleSessionExpired);
  }, [navigate]);

  // Load user on app start
  useEffect(() => {
    const loadUser = async () => {
      try {
        if (authService.isAuthenticated()) {
          const user = await authService.getProfile();
          const token = authService.getCurrentToken();

          if (user && token) {
            dispatch({
              type: 'LOAD_USER',
              payload: { user, token }
            });
            return;
          }
        }
        dispatch({ type: 'LOAD_ERROR' });
      } catch (error) {
        console.error('Failed to load user:', error);
        dispatch({ type: 'LOAD_ERROR' });

        // Clear invalid data
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
      }
    };

    loadUser();
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    try {
      dispatch({ type: 'LOGIN_START' });
      
      const response = await authService.login(credentials);
      
      // Store in localStorage
      localStorage.setItem('auth_token', response.token);
      localStorage.setItem('auth_user', JSON.stringify(response.user));
      
      dispatch({ 
        type: 'LOGIN_SUCCESS', 
        payload: { 
          user: response.user, 
          token: response.token 
        } 
      });
    } catch (error) {
      console.error('Login failed:', error);
      dispatch({ type: 'LOGIN_ERROR' });
      
      // Re-throw to allow components to handle the error
      if (error instanceof AuthError) {
        throw error;
      }
      throw new AuthError('Login failed', 'UnknownError');
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with logout even if API call fails
    } finally {
      // Clear localStorage and update state
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      dispatch({ type: 'LOGOUT' });
    }
  }, []);

  const refreshToken = useCallback(async () => {
    try {
      const response = await authService.refreshToken();
      
      // Update localStorage
      localStorage.setItem('auth_token', response.token);
      localStorage.setItem('auth_user', JSON.stringify(response.user));
      
      dispatch({ 
        type: 'LOGIN_SUCCESS', 
        payload: { 
          user: response.user, 
          token: response.token 
        } 
      });
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Force logout if refresh fails
      await logout();
      throw error;
    }
  }, [logout]);

  const setAuthData = useCallback((user: User, token: string) => {
    dispatch({ 
      type: 'LOGIN_SUCCESS', 
      payload: { user, token } 
    });
  }, []);

  const contextValue: AuthContextType = {
    authState,
    login,
    logout,
    refreshToken,
    setAuthData,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Export a wrapper that can be used without Router (Router will be added in App)
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <AuthProviderInner>{children}</AuthProviderInner>;
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper hooks
export const useUser = () => {
  const { authState } = useAuth();
  return authState.user;
};

export const useIsAuthenticated = () => {
  const { authState } = useAuth();
  return authState.isAuthenticated;
};

export const useAuthLoading = () => {
  const { authState } = useAuth();
  return authState.isLoading;
};